import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExcelExporter, ExcelExportOptions } from '@/utils/excelExport';
import { formatDateAsYearMonthDayHourMinute } from '@/utils/formatters/dateFormatter';
import { formatTimezoneShort } from '@/utils/formatters/timezoneFormatter';
import { useToast } from '@/contexts/ToastContext';
import { formatVehicleTypeForDisplay } from '@/config/deviceTypes.config';
import i18n from '@/i18n/i18n';

export interface UseExcelExportOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  showToasts?: boolean; // Controlar si mostrar toasts automáticamente
}

/**
 * Hook personalizado para manejar exportaciones a Excel
 */
export const useExcelExport = (options: UseExcelExportOptions = {}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { onSuccess, onError, showToasts = true } = options;
  const [isExporting, setIsExporting] = useState(false);

  const exportToExcel = useCallback(
    async (exportOptions: ExcelExportOptions) => {
      try {
        setIsExporting(true);
        await ExcelExporter.exportToExcel(exportOptions);

        if (showToasts) {
          showToast(t('common.exportSuccessful'), 'success');
        }
        onSuccess?.();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        console.error('Error al exportar a Excel:', error);

        if (showToasts) {
          showToast(`❌ Error al exportar a Excel: ${errorMessage}`, 'error');
        }
        onError?.(error instanceof Error ? error : new Error(errorMessage));
      } finally {
        setIsExporting(false);
      }
    },
    [t, showToast, onSuccess, onError, showToasts]
  );

  const exportLectures = useCallback(
    async (lectures: any[], fileName: string, deviceName?: string) => {
      if (lectures.length === 0) {
        if (showToasts) {
          showToast(t('common.noDataToExport'), 'warning');
        }
        return;
      }

      const columns = ExcelExporter.createLecturesColumns();
      const data = lectures.map((lecture) => {
        const lat = lecture.coordinates?.latitude ?? (lecture as any).coordinates?.lat;
        const long = lecture.coordinates?.longitude ?? (lecture as any).coordinates?.long;
        return {
          lectureId: lecture.lectureIdentifier || lecture.id,
          operationId: lecture.operationId || '',
          sequence: lecture.sequence || '',
          timestamp: lecture.timestamp,
          datetime: formatDateAsYearMonthDayHourMinute(lecture.timestamp, lecture.tz),
          timezoneShort: formatTimezoneShort(lecture.tz),
          latitude: lat,
          longitude: long,
          eventType: lecture.eventType,
          angleLateral: lecture.inclinationAngle_lateral,
          angleLongitudinal: lecture.inclinationAngle_longitudinal,
          limitLateral: lecture.angleLimits_lateral,
          limitLongitudinal: lecture.angleLimits_longitudinal,
        };
      });

      await exportToExcel({
        fileName,
        sheetName: 'Lecturas del Dispositivo',
        columns,
        data,
      });
    },
    [exportToExcel, showToast, t, showToasts]
  );

  const exportDevices = useCallback(
    async (devices: any[], fileName: string, user?: { id?: string }) => {
      if (devices.length === 0) {
        if (showToasts) {
          showToast(t('common.noDataToExport'), 'warning');
        }
        return;
      }

      const columns = ExcelExporter.createDevicesColumns();
      const data = devices.map((device) => {
        const connTz = device.lastConnectionTz ?? 'UTC';
        const opTz = device.lastOperationTz ?? 'UTC';
        const rawType = device.type || device.vehicle?.type || '';
        const lang = i18n.language === 'es' ? 'es' : 'en';
        return {
          serialNumber: device.serialNumber || '',
          plate: device.plate || device.vehicle?.plate || '',
          alias: device.alias || '',
          type: formatVehicleTypeForDisplay(rawType, lang),
          status: device.status || '',
          lastConnection: device.lastConnection
            ? formatDateAsYearMonthDayHourMinute(device.lastConnection, connTz)
            : 'N/A',
          lastConnectionZone: formatTimezoneShort(connTz),
          lastOperation: device.lastOperation
            ? formatDateAsYearMonthDayHourMinute(device.lastOperation, opTz)
            : 'N/A',
          lastOperationZone: formatTimezoneShort(opTz),
          operationCount: device.operationCount || 0,
        };
      });

      await exportToExcel({
        fileName,
        sheetName: 'Dispositivos',
        columns,
        data,
      });
    },
    [exportToExcel, showToast, t, showToasts]
  );

  const exportOperations = useCallback(
    async (operations: any[], fileName: string, deviceName?: string) => {
      if (operations.length === 0) {
        if (showToasts) {
          showToast(t('common.noDataToExport'), 'warning');
        }
        return;
      }

      const columns = ExcelExporter.createOperationsColumns();
      const data = operations.map((operation) => ({
        operationId: operation.operationIdentifier || operation.id,
        startDate: operation.startTimestamp
          ? formatDateAsYearMonthDayHourMinute(operation.startTimestamp, (operation as any).tz)
          : 'N/A',
        endDate: operation.endTimestamp
          ? formatDateAsYearMonthDayHourMinute(operation.endTimestamp, (operation as any).tz)
          : 'N/A',
        duration: operation.durationMinutes || 0,
        lectureCount: operation.lectureCount || 0,
        centroidLat: operation.centroid_latitutde || '',
        centroidLng: operation.centroid_longitude || '',
      }));

      await exportToExcel({
        fileName,
        sheetName: 'Operaciones',
        columns,
        data,
      });
    },
    [exportToExcel, showToast, t, showToasts]
  );

  return {
    exportToExcel,
    exportLectures,
    exportDevices,
    exportOperations,
    generateFileName: ExcelExporter.generateFileName,
    isExporting,
  };
};
